/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

interface TrackingProps {
	locale?: string;
	/** Override for NEXT_PUBLIC_TRACKING_BASE */
	trackingBase?: string;
	/** Override for NEXT_PUBLIC_TRACKING_DISABLED */
	trackingDisabled?: string;
	/** Override for NEXT_PUBLIC_DSA_TRACKING_ID */
	dsaTrackingId?: string;
	/** Override for NEXT_PUBLIC_TRACKING_VERSION */
	trackingVersion?: string;
	/** Override for NEXT_PUBLIC_TRACKING_SCRIPT_HASH */
	trackingScriptHash?: string;
}

export const Tracking = ({
	locale,
	trackingBase,
	trackingDisabled,
	dsaTrackingId,
	trackingVersion,
	trackingScriptHash,
}: TrackingProps): React.JSX.Element | null => {
	const resolvedDisabled = trackingDisabled ?? process.env.NEXT_PUBLIC_TRACKING_DISABLED;
	const resolvedBase = trackingBase ?? process.env.NEXT_PUBLIC_TRACKING_BASE;
	const resolvedDsaId = dsaTrackingId ?? process.env.NEXT_PUBLIC_DSA_TRACKING_ID;
	const resolvedVersion = trackingVersion ?? process.env.NEXT_PUBLIC_TRACKING_VERSION;
	const resolvedHash = trackingScriptHash ?? process.env.NEXT_PUBLIC_TRACKING_SCRIPT_HASH;

	const trackingEnabled = Boolean((!resolvedDisabled || resolvedDisabled === "false") && resolvedBase);

	if (trackingEnabled) {
		// `resolvedBase` may be absolute (e.g. https://tracking.epicgames.com) or
		// root-relative (e.g. /tracking-api when the script is served first-party
		// via the tracking-site target group). Build the src by string so both
		// forms work; only `URLSearchParams` is used, for query encoding.
		const params = new URLSearchParams();

		if (resolvedDsaId) {
			params.set("p", resolvedDsaId);
		}

		if (locale) {
			params.set("l", locale);
		}

		const withVersion = Boolean(resolvedVersion && resolvedHash);
		if (withVersion) {
			params.set("version", resolvedVersion as string);
		}

		const base = (resolvedBase ?? "").replace(/\/+$/, "");
		const query = params.toString();
		const src = `${base}/tracking.js${query ? `?${query}` : ""}`;

		if (withVersion) {
			return <script key="tracking-script" crossOrigin="anonymous" integrity={resolvedHash} src={src} async />;
		}

		return <script key="tracking-script" src={src} async />;
	}

	return null;
};
